import { FC } from 'react';
import DocumentBodyHeader from '../DocumentBodyHeader/DocumentBodyHeader';
import DocumentCard from '../DocumentCard/DocumentCard';
import { Document as DocumentType, SortingType } from '@/lib/types/apiTypes';
import { motion } from 'framer-motion';
import { iCSlideInDelay } from '@/constants/variants';
import SecondaryButton from '../ui/shared/Button/SecondaryButton/SecondaryButton';
import Processing from '../ui/icons/Processing';
import { cn } from '@/lib/utils';
import { Plus } from 'lucide-react';
import { useAppDispatch } from '@/lib/hooks/hooks';
import { openDialog } from '@/redux/slices/pageSlice';
import DocumentsEmptyUpload from '../ui/icons/new/DocumentsEmptyUpload';

interface DocumentBodyProps {
	documents: DocumentType[];
	documentsCount: number;
	isFetchingNextPage: boolean;
	showLoadMore?: boolean;
	fetchNextPage: () => void;
	sortOrder: SortingType;
	handleSorting?: (sortOrder: SortingType) => void;
}

const DocumentBody: FC<DocumentBodyProps> = ({
	documents,
	documentsCount,
	isFetchingNextPage = false,
	showLoadMore = false,
	fetchNextPage,
	sortOrder,
	handleSorting,
}) => {
	const dispatch = useAppDispatch();

	return (
		<div className="flex-col h-full">
			<DocumentBodyHeader
				docCount={documentsCount || documents?.length}
				sortOrder={sortOrder}
				handleSorting={handleSorting}
			/>
			<div className="h-5 z-10 w-full bg-gradient-to-b from-[#f5f5f5] to-[#f5f5f500]"></div>
			<motion.div
				className="relative grid h-full overflow-y-auto hidden-scroll mt-[-1rem] pb-8"
				key="prFooterDelay0"
				variants={iCSlideInDelay}
				initial="initial"
				animate="animate"
				exit="exit"
			>
				{documentsCount === 0 ? (
					<div className="flex h-full justify-center items-center">
						<div className="flex flex-col w-[340px] gap-8">
							<DocumentsEmptyUpload />
							<h4 className="text-black text-center dynamic-xl-large">
								No document found, add a new document
							</h4>
							<SecondaryButton
								className="flex w-fit self-center bg-transparent"
								onClick={() =>
									dispatch(openDialog({ type: 'uploadDocuments' }))
								}
							>
								<Plus />
								Add New Document
							</SecondaryButton>
						</div>
					</div>
				) : (
					<>
						<div className="grid grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 px-6 xl:px-8 py-6 gap-4 h-fit">
							{documents?.map((document) => (
								<DocumentCard key={document.id} documentCard={document} />
							))}
						</div>
						{showLoadMore && (
							<SecondaryButton
								className={cn(
									'w-40 justify-self-center',
									isFetchingNextPage ? 'pointer-events-none' : ''
								)}
								onClick={() => !isFetchingNextPage && fetchNextPage()}
							>
								{isFetchingNextPage ? (
									<>
										<Processing className="processing-animation" />
										Loading
									</>
								) : (
									<>Load More</>
								)}
							</SecondaryButton>
						)}
					</>
				)}
			</motion.div>
		</div>
	);
};

export default DocumentBody;
